import React from "react";
import { useCurrentFrame, useVideoConfig } from "remotion";
import { TextOverlay } from "@/components/editor/types";
import { animationTemplates } from "@/components/editor/templates/animation-templates";


interface TextLayerContentProps {
  overlay: TextOverlay;
}

export const getLayerStyles = ({overlay, frame, fps} : {overlay: TextOverlay, frame: number, fps: number}) => {
  // Calculate if we're in the exit phase (last 30 frames)
  const isExitPhase = frame >= overlay.durationInFrames - 30;

  /**
   * Apply enter animation only during entry phase
   */
  const enterAnimation =
      !isExitPhase && overlay.styles.animation?.enter
        ? animationTemplates[overlay.styles.animation?.enter?.name ?? ""]?.enter?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.enter
          )
        : {};
  /**
   * Apply loop animation only during entry phase
   */
  const loopAnimation = animationTemplates[overlay.styles.animation?.loop?.name ?? ""]?.loop?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.loop
          )
        ?? {};

  /**
   * Apply exit animation only during exit phase
   */
  const exitAnimation =
      isExitPhase && overlay.styles.animation?.exit
        ? animationTemplates[overlay.styles.animation?.exit?.name ?? ""]?.exit?.(
            frame,
            overlay.durationInFrames,
            fps,
            overlay.styles.animation?.exit
          )
        : {};

  
    const [ textAlign,alignItems ] = overlay.styles.textAlign?.split?.("-") || ["center","center"];

  const containerStyle: React.CSSProperties = {
    width: "100%",
    height: "100%",
    display: "flex",
    alignItems: alignItems || "center",
    textAlign: textAlign as React.CSSProperties["textAlign"],
    justifyContent:
      textAlign === "center"
        ? "center"
        : textAlign === "right"
        ? "flex-end"
        : "flex-start",
    overflow: "hidden",
    ...(isExitPhase ? exitAnimation : enterAnimation),
    ...loopAnimation
  };

  const { ...restStyles } = overlay.styles;
  
  const textStyle: React.CSSProperties = {
    ...restStyles,
    animation: undefined,
    fontSize: overlay.styles.fontSize,
    fontFamily: overlay.styles.fontFamily,
    maxWidth: "100%",
    wordWrap: "break-word",
    whiteSpace: "pre-wrap",
    lineHeight: "1.2",
    padding: "0.1em",
    ...(isExitPhase ? exitAnimation : enterAnimation),
    ...loopAnimation
  };
  return {
    containerStyle,
    textStyle,
  }
}

export const TextLayerContent: React.FC<TextLayerContentProps> = ({
  overlay,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  const { containerStyle, textStyle } = getLayerStyles({overlay, frame, fps});

  return (
    <div style={containerStyle}>
      <div style={{...textStyle, userSelect: "none"}}>{overlay.content}</div>
    </div>
  );
};
